import { useState, useEffect, useCallback } from 'react';

export const useLocation = () => {
    const [location, setLocation] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [permission, setPermission] = useState('prompt'); // 'granted', 'denied', 'prompt'

    // Check permission status
    useEffect(() => {
        if ('permissions' in navigator) {
            navigator.permissions.query({ name: 'geolocation' }).then((result) => {
                setPermission(result.state);
                result.onchange = () => setPermission(result.state);
            });
        }
    }, []);

    const requestLocation = useCallback(() => {
        if (!navigator.geolocation) {
            setError('Geolocation is not supported by your browser');
            return;
        }

        setLoading(true);
        setError(null);

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setLocation({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy
                });
                setLoading(false);
                setPermission('granted');
                
                // Store in localStorage for persistence
                localStorage.setItem('sapa_user_location', JSON.stringify({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    timestamp: Date.now()
                }));
            },
            (err) => {
                setError(err.message);
                setLoading(false);
                if (err.code === 1) {
                    setPermission('denied');
                }
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000 // 5 minutes cache
            }
        );
    }, []);

    // Try to load from localStorage on mount
    useEffect(() => {
        const stored = localStorage.getItem('sapa_user_location');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                // Use stored location if less than 1 hour old
                if (Date.now() - parsed.timestamp < 3600000) {
                    setLocation({
                        latitude: parsed.latitude,
                        longitude: parsed.longitude
                    });
                }
            } catch (e) {
                console.error('Error parsing stored location:', e);
            }
        }
    }, []);

    const clearLocation = useCallback(() => {
        setLocation(null);
        localStorage.removeItem('sapa_user_location');
    }, []);

    return {
        location,
        error,
        loading,
        permission,
        requestLocation,
        clearLocation,
        hasLocation: !!location
    };
};

export default useLocation;
